import React, { useCallback, useEffect, useRef, useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  getTokenListings,
  getTokenListing,
  relistTokenListing,
  delistTokenListing,
  deleteTokenListing,
  approveTokenSponsorship,
  revokeTokenSponsorship,
} from "../../helpers/fakebackend_helper";
import { usePermission } from "../../helpers/usePermission";
import { PAGE_SIZE, PERM_MANAGE, PERM_SPONSOR } from "./constants";
import { describeError } from "./utils";
import ListingFilters, { EMPTY_FILTERS } from "./components/ListingFilters";
import ListingsTable from "./components/ListingsTable";
import ListingForm from "./components/ListingForm";
import ConfirmDialog from "./components/ConfirmDialog";
import { btnPrimary } from "./components/Panel";

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

// Confirm-dialog copy per action. Delist asks for an optional reason.
const CONFIRM = {
  delist: (l) => ({
    title: `Delist ${l.symbol}`,
    message: "The token leaves search, market lists, swap pickers and trade actions. Holders still see it, marked as not actively listed. You can relist it later.",
    confirmLabel: "Delist",
    danger: true,
    withReason: true,
  }),
  relist: (l) => ({
    title: `Relist ${l.symbol}`,
    message: "The token becomes active again and returns to search and market lists.",
    confirmLabel: "Relist",
  }),
  publish: (l) => ({
    title: `Publish ${l.symbol}`,
    message: "The draft becomes active and appears in search and market lists.",
    confirmLabel: "Publish",
  }),
  delete: (l) => ({
    title: `Delete draft ${l.symbol}`,
    message: "The draft is removed permanently. This cannot be undone.",
    confirmLabel: "Delete",
    danger: true,
  }),
  approve: (l) => ({
    title: `Approve sponsorship for ${l.symbol}`,
    message: `The Sponsored tag for ${l.sponsor_name || "this sponsor"} shows in the selected placements while the campaign window is open.`,
    confirmLabel: "Approve",
  }),
  revoke: (l) => ({
    title: `Revoke sponsorship for ${l.symbol}`,
    message: "The Sponsored tag is removed from the app. The campaign stays on the listing and can be approved again.",
    confirmLabel: "Revoke",
    danger: true,
  }),
};

const SUCCESS = {
  delist: "Listing delisted.",
  relist: "Listing relisted.",
  publish: "Listing published.",
  delete: "Draft deleted.",
  approve: "Sponsorship approved.",
  revoke: "Sponsorship revoked.",
};

const TokenListings = () => {
  document.title = "Token Listings | BiorGov Admin";
  const can = usePermission();
  const canManage = can(PERM_MANAGE);
  const canSponsor = can(PERM_SPONSOR);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const debouncedSearch = useDebounced(filters.search, 300);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ listings: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const reqId = useRef(0);

  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((kind, text) => {
    setToast({ kind, text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);
  useEffect(() => () => toastTimer.current && clearTimeout(toastTimer.current), []);

  // Form panel: { mode, listing } (listing kept in state so the reference is stable).
  const [formState, setFormState] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action, listing, busy, error }
  const [busyId, setBusyId] = useState(null);

  const { status, network_id, category, featured, sponsored } = filters;
  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (status) params.status = status;
    if (network_id !== "") params.network_id = network_id;
    if (category) params.category = category;
    if (featured) params.featured = featured;
    if (sponsored) params.sponsored = sponsored;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    getTokenListings(params)
      .then((res) => {
        if (id !== reqId.current) return;
        setData({
          listings: Array.isArray(res && res.listings) ? res.listings : [],
          total: Number((res && res.total) || 0),
          total_pages: Math.max(1, Number((res && res.total_pages) || 1)),
        });
        setError("");
      })
      .catch((err) => {
        if (id !== reqId.current) return;
        setError(describeError(err, "Listings could not be loaded. Try again."));
      })
      .finally(() => {
        if (id === reqId.current) setLoading(false);
      });
  }, [page, status, network_id, category, featured, sponsored, debouncedSearch, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const onFilters = (next) => {
    setFilters(next);
    setPage(1);
  };

  const openEdit = async (listingOrId) => {
    const id = typeof listingOrId === "object" ? listingOrId.id : listingOrId;
    setBusyId(id);
    try {
      // Fetch the full admin record so the form edits the latest version.
      const res = await getTokenListing(id);
      const listing = (res && res.listing) || (typeof listingOrId === "object" ? listingOrId : null);
      if (listing) setFormState({ mode: "edit", listing });
      else showToast("error", "This listing no longer exists. Refresh the list.");
    } catch (err) {
      showToast("error", describeError(err, "The listing could not be opened. Try again."));
    } finally {
      setBusyId(null);
    }
  };

  const onAction = (action, listing) => {
    if (action === "edit") {
      openEdit(listing);
      return;
    }
    const make = CONFIRM[action];
    if (make) setConfirm({ action, listing, busy: false, error: "", ...make(listing) });
  };

  const runConfirm = async (reason) => {
    if (!confirm) return;
    const { action, listing } = confirm;
    setConfirm((c) => ({ ...c, busy: true, error: "" }));
    setBusyId(listing.id);
    try {
      if (action === "delist") await delistTokenListing(listing.id, reason || undefined);
      else if (action === "relist" || action === "publish") await relistTokenListing(listing.id);
      else if (action === "delete") await deleteTokenListing(listing.id);
      else if (action === "approve") await approveTokenSponsorship(listing.id);
      else if (action === "revoke") await revokeTokenSponsorship(listing.id);
      setConfirm(null);
      showToast("success", SUCCESS[action] || "Done.");
      reload();
    } catch (err) {
      setConfirm((c) => (c ? { ...c, busy: false, error: describeError(err) } : c));
    } finally {
      setBusyId(null);
    }
  };

  const onSaved = (listing, mode) => {
    setFormState(null);
    showToast("success", mode === "edit" ? "Listing saved." : listing && listing.status === "active" ? "Listing created and active." : "Listing created as a draft.");
    reload();
  };

  const { listings, total, total_pages } = data;

  return (
    <div className="min-h-screen min-w-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full min-w-0">
        <BreadCrumb title="Token listings" pageTitle="In the app" />

        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <p className="text-sm text-gray-400 max-w-2xl">
            List custom tokens in the app, feature them on Home (the Featured row under Explore), mark them verified,
            run sponsored placements on Market › Tokens and Home holdings, and delist them.
            <span className="block mt-2 text-amber-200">
              Not in the current app yet: the Featured row, Sponsored and Verified marks, categories and delisting
              arrive with the app update that draws listings. Until then only a listing's name, symbol, logo and
              decimals reach the app.
            </span>
          </p>
          {canManage && (
            <button type="button" className={btnPrimary} onClick={() => setFormState({ mode: "create", listing: null })}>
              Add listing
            </button>
          )}
        </div>

        <ListingFilters value={filters} onChange={onFilters} onReset={() => onFilters(EMPTY_FILTERS)} />

        <div className="bl-glass overflow-hidden">
          {error && (
            <div className="p-4 border-b border-red-500/30 bg-red-500/10 text-sm text-red-300 flex items-center justify-between gap-3">
              <span>{error}</span>
              <button type="button" className="text-xs underline" onClick={reload}>Retry</button>
            </div>
          )}
          <ListingsTable
            listings={listings}
            loading={loading}
            canManage={canManage}
            canSponsor={canSponsor}
            busyId={busyId}
            onAction={onAction}
          />
          <div className="px-4 py-3 text-xs text-gray-400 border-t border-gray-700 flex items-center justify-between gap-3 flex-wrap">
            <span>
              {total.toLocaleString()} listing{total === 1 ? "" : "s"}
              {total_pages > 1 ? `, page ${page} of ${total_pages}` : ""}
            </span>
            {total_pages > 1 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 border border-white/10 rounded-md text-gray-200 disabled:opacity-40"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 border border-white/10 rounded-md text-gray-200 disabled:opacity-40"
                  disabled={page >= total_pages || loading}
                  onClick={() => setPage((p) => Math.min(total_pages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ListingForm
        open={Boolean(formState)}
        mode={formState ? formState.mode : "create"}
        listing={formState ? formState.listing : null}
        onClose={() => setFormState(null)}
        onSaved={onSaved}
        onOpenExisting={(id) => {
          setFormState(null);
          openEdit(id);
        }}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm ? confirm.title : ""}
        message={confirm ? confirm.message : ""}
        confirmLabel={confirm ? confirm.confirmLabel : "Confirm"}
        danger={Boolean(confirm && confirm.danger)}
        withReason={Boolean(confirm && confirm.withReason)}
        busy={Boolean(confirm && confirm.busy)}
        error={confirm ? confirm.error : ""}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
      />

      {toast && (
        <div className="fixed bottom-6 right-6 z-[1100] max-w-[min(22rem,calc(100vw-3rem))]" role="status">
          <div
            className={`px-4 py-3 rounded-md shadow-lg text-sm border break-words ${
              toast.kind === "success"
                ? "bg-green-500/12 border-green-500/30 text-green-300"
                : toast.kind === "error"
                ? "bg-red-500/12 border-red-500/30 text-red-300"
                : "bg-bl-accent/8 border-gray-700 text-gray-200"
            }`}
          >
            {toast.text}
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenListings;
